import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Package } from 'lucide-react';
import { useShiftReportTotals, type FieldTotal } from '@/hooks/useShiftReportTotals';
import { useAuth } from '@/contexts/AuthContext';
import { useSites } from '@/hooks/useSites';

const Column: React.FC<{ title: string; rows: FieldTotal[] }> = ({ title, rows }) => (
  <div className="space-y-2">
    <div className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">{title}</div>
    {rows.length === 0 ? (
      <div className="text-sm text-muted-foreground/70 italic">No data</div>
    ) : (
      <ul className="space-y-1.5">
        {rows.map((row) => (
          <li key={row.fieldId} className="flex items-baseline justify-between gap-3 border-b border-border/40 pb-1">
            <span className="text-sm text-foreground">{row.label}</span>
            <span className="text-sm font-semibold tabular-nums">{row.total.toLocaleString()}</span>
          </li>
        ))}
      </ul>
    )}
  </div>
);

export const DistributionTotalsCard: React.FC = () => {
  const { userSites } = useAuth();
  const { sites = [] } = useSites();
  // Default to user-assigned sites; fall back to all org-visible sites (RLS scopes anyway)
  const siteIds = userSites.length > 0 ? userSites : sites.map((s: any) => s.id);
  const { today, week, month, isLoading } = useShiftReportTotals(siteIds);

  return (
    <Card className="animate-fade-in">
      <CardHeader>
        <CardTitle className="text-base flex items-center gap-2">
          <Package className="h-4 w-4 text-primary" />
          Distribution Totals
          <span className="ml-2 text-xs font-normal text-muted-foreground">
            Sums of numeric Shift Report fields
          </span>
        </CardTitle>
      </CardHeader>
      <CardContent>
        {isLoading ? (
          <div className="text-sm text-muted-foreground">Loading totals…</div>
        ) : today.length === 0 && week.length === 0 && month.length === 0 ? (
          <div className="rounded-md border border-border/50 bg-muted/30 p-4 text-sm text-muted-foreground">
            No shift report values recorded yet. Once staff fill in numeric fields (bags, water, care kits, etc.), totals will appear here.
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <Column title="Today" rows={today} />
            <Column title="This Week" rows={week} />
            <Column title="This Month" rows={month} />
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default DistributionTotalsCard;
